/* eslint-disable @angular-eslint/prefer-inject */
import { AuditDataService } from '../infrastructure/audits/audit.data.service';
import { AuditCreate, AuditDto, AuditRead, AuditSearchParams, } from '../entities/audit.dto';
import { AuditStore } from '../infrastructure/audits/audit.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { AuditMapper } from '../infrastructure/audits/audit.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { WarehouseFacade } from './warehouse.facade';
import { IQueryParams, PFWmsEntities, ProfileProvider, } from '@pf/shared-common';
import { ContainerDto } from '../entities/container.dto';
import { map, Observable, switchMap } from 'rxjs';
import { AuditCount, AuditStatus, ResourceAuth, } from '@control-tower/platform-wms';

@Injectable()
class AuditFacade extends AbstractManageEntityFacade<
    AuditDto,
    AuditRead,
    AuditCreate,
    AuditSearchParams
> {
    private readonly profileProvider = inject(ProfileProvider);
    private readonly warehouseFacade = inject(WarehouseFacade);

    nameField: keyof AuditDto = 'id';

    constructor(
        dataService: AuditDataService,
        store: AuditStore,
        mapper: AuditMapper
    ) {
        super(dataService, null, store, mapper);
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof AuditDto, string>> {
        return { id: searchText };
    }

    override getDefaultSearchParams$(
        params: IQueryParams<ContainerDto>
    ): Observable<IQueryParams<ContainerDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: AuditCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    listenForAuditUpdates() {
        this.listenForRealTimeEvents(PFWmsEntities.Audit, {
            processEventsIfNotInStore: true,
        });
    }

    getActiveAudits$() {
        return this.searchAll$(
            {
                pageIndex: 1,
                pageSize: 50,
                filters: {
                    status: [AuditStatus.New],
                },
            },
            false,
            100
        ).pipe(
            switchMap(() => {
                return this.store.filter$((audit: AuditRead) => {
                    return (
                        audit.warehouse?.id ===
                            this.warehouseFacade.active!.id &&
                        audit.status === AuditStatus.New
                    );
                });
            }),
            map((audits: AuditRead[]) => this.mapper.toDTOList(audits))
        );
    }

    getUserActiveAudits$() {
        return this.searchAll$(
            {
                pageIndex: 1,
                pageSize: 50,
                filters: {
                    status: [AuditStatus.InProgress],
                    assignedTo: this.profileProvider.profile.userId,
                },
            },
            false
        ).pipe(
            switchMap(() => {
                return this.store.filter$((audit: AuditRead) => {
                    return (
                        audit.warehouse?.id ===
                            this.warehouseFacade.active!.id &&
                        audit.status === AuditStatus.InProgress &&
                        audit.assignedTo === this.profileProvider.profile.userId
                    );
                });
            }),
            map((audits: AuditRead[]) => this.mapper.toDTOList(audits))
        );
    }

    claimAudits$(auditIds: string[]) {
        return (this.dataService as AuditDataService).claimAudits$(
            this.warehouseFacade.active!.id,
            auditIds
        );
    }

    releaseAudits$(auditIds: string[]) {
        return (this.dataService as AuditDataService).releaseAudits$(
            this.warehouseFacade.active!.id,
            auditIds
        );
    }

    addAuditCount$(auditId: string, auditCount: AuditCount) {
        const audit = this.getById(auditId);
        if (!audit) {
            throw new Error('Audit not found');
        }
        const sanitizeCount = (c: AuditCount): AuditCount => ({
            auditQuantity: c.auditQuantity,
            actualQuantity: c.actualQuantity,
            performedBy: c.performedBy,
            performedOn: c.performedOn,
        });
        audit.auditCounts = [
            ...audit.auditCounts.map(sanitizeCount),
            sanitizeCount(auditCount),
        ];
        return this.save$(audit);
    }

    completeAudit$(auditId: string) {
        const audit = this.getById(auditId);
        if (!audit) {
            throw new Error('Audit not found');
        }
        audit.status = AuditStatus.Completed;
        return this.save$(audit);
    }
}

export default AuditFacade;
